const UTM_KEY = 'utm_data';
const UTM_TTL_DAYS = 30;

interface UtmData {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
  utm_term: string;
  landing_time: string;
  /**
   * True only when the traffic is verified KOL traffic:
   * utm_source starts with `kol_` / equals `kol` (從 /kol/:code 導入),
   * or utm_campaign matches an existing kol_profiles.kol_code.
   * General marketing campaigns (e.g. utm_source=facebook&utm_campaign=9月新品)
   * must stay false so they are never attributed to a KOL.
   */
  is_kol?: boolean;
}

export function captureUtmParams(): void {
  const params = new URLSearchParams(window.location.search);
  const source = params.get('utm_source');
  const medium = params.get('utm_medium');
  const campaign = params.get('utm_campaign');
  const content = params.get('utm_content');
  const term = params.get('utm_term');

  if (!source && !medium && !campaign && !content && !term) return;

  const data: UtmData = {
    utm_source: source || '',
    utm_medium: medium || '',
    utm_campaign: campaign || '',
    utm_content: content || '',
    utm_term: term || '',
    landing_time: new Date().toISOString(),
    is_kol: isKolSource(source || ''),
  };

  localStorage.setItem(UTM_KEY, JSON.stringify(data));
}

export function getUtmData(): UtmData | null {
  const raw = localStorage.getItem(UTM_KEY);
  if (!raw) return null;

  try {
    const data: UtmData = JSON.parse(raw);
    const landingTime = new Date(data.landing_time).getTime();
    const now = Date.now();
    const ttlMs = UTM_TTL_DAYS * 24 * 60 * 60 * 1000;

    if (now - landingTime > ttlMs) {
      localStorage.removeItem(UTM_KEY);
      return null;
    }

    return {
      utm_content: '',
      utm_term: '',
      ...data,
    };
  } catch {
    localStorage.removeItem(UTM_KEY);
    return null;
  }
}

export function extractKolCode(utmSource: string): string | null {
  if (!utmSource || !utmSource.startsWith('kol_')) return null;
  return utmSource.slice(4) || null;
}

/** utm_source that unambiguously identifies KOL traffic. */
export function isKolSource(utmSource: string): boolean {
  const s = (utmSource || '').toLowerCase();
  return s === 'kol' || s.startsWith('kol_');
}

/** Persist that the stored UTM data was verified as KOL traffic. */
export function markUtmAsKol(): void {
  const data = getUtmData();
  if (!data || data.is_kol) return;
  localStorage.setItem(UTM_KEY, JSON.stringify({ ...data, is_kol: true }));
}

/**
 * Resolve the KOL code for the stored UTM data, or null for general campaigns.
 * `kol_<code>` source wins; otherwise the campaign value is only treated as a
 * KOL code once it has been verified (is_kol).
 */
export function resolveKolCode(data: UtmData | null): string | null {
  if (!data) return null;
  const fromSource = extractKolCode(data.utm_source);
  if (fromSource) return fromSource;
  if (data.is_kol && data.utm_campaign) return data.utm_campaign;
  return null;
}

/** GA4 client_id from the `_ga` cookie (format: GA1.1.<id>.<ts>). */
export function getGaClientId(): string | null {
  try {
    const m = document.cookie.match(/(?:^|;\s*)_ga=GA\d+\.\d+\.([^;]+)/);
    return m?.[1] || null;
  } catch {
    return null;
  }
}
